import React, { startTransition, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, noop, isEqual } from 'lodash-es';
import debounce from 'lodash-es/debounce';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Button from 'reactstrap/lib/Button';
import Form from 'reactstrap/lib/Form';
import { TrackingHandler } from 'client/tracking/handler';
import { TrackingConstant } from 'client/tracking/constant';
import {
  PRICE_CHECKER_CREATIVE_ID,
  PRICE_ERRORS,
  PRICE_THRESHOLD,
  PriceCheckerEntities,
} from 'site-modules/shared/constants/price-checker';
import { ENTER_KEY_VALUE } from 'site-modules/shared/constants/key-values';
import { getSharedDealPropsForPriceForm } from 'site-modules/shared/utils/price-checker/community-share-utils';
import { PaymentInput } from 'site-modules/shared/components/calculator/payment-input/payment-input';
import { DealerSelection } from 'site-modules/shared/components/price-checker/dealer-selection/dealer-selection';

import './prices-form.scss';

function PricesFormUI({
  chartPrices,
  isDealRated = false,
  defaultMsrp,
  sharedWithCommunityProps = null,
  resetSharedDealParams = noop,
  setChartPrices,
  className = null,
  innerRef = null,
  readOnlyMsrp = false,
  disabled = false,
  isMobile = false,
  mode = '',
  isCore6830International = false,
  onChangeDealer = noop,
  ...restProps
}) {
  const [submitErrors, setSubmitErrors] = useState({
    userMsrp: null,
    userDealerPrice: null,
    dealerSelectError: null,
  });
  // inputErrors block form submit
  const [inputErrors, setInputErrors] = useState({
    userMsrp: null,
    userDealerPrice: null,
  });
  const [inputValues, setInputValues] = useState({
    userMsrp: 0,
    userDealerPrice: 0,
  });
  const errors = {
    userMsrp: inputErrors.userMsrp || submitErrors.userMsrp,
    userDealerPrice: inputErrors.userDealerPrice || submitErrors.userDealerPrice,
    dealerSelectError: submitErrors.dealerSelectError,
  };
  const isSubmitDisabled = disabled || Object.values(inputErrors).some(error => error);
  const communityProps = getSharedDealPropsForPriceForm({
    ...{
      chartPrices,
      isDealRated,
      defaultMsrp,
      sharedWithCommunityProps,
      resetSharedDealParams,
      setChartPrices,
      className,
      innerRef,
      readOnlyMsrp,
      disabled,
      isMobile,
      mode,
      isCore6830International,
      ...restProps,
      onChangeDealer,
    },
    inputValues,
  });

  const { userMsrp, userDealerPrice } = chartPrices;
  useEffect(() => {
    if (inputValues.userMsrp === userMsrp && inputValues.userDealerPrice === userDealerPrice) {
      return;
    }
    startTransition(() => {
      setInputValues({ userMsrp, userDealerPrice });
      setSubmitErrors({
        userMsrp: null,
        userDealerPrice: null,
      });
      setInputErrors({
        userMsrp: null,
        userDealerPrice: null,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userMsrp, userDealerPrice]);

  const onInputBlur = useCallback(
    ({ target: { name, value } }) => {
      const valueNumber = Number(value);
      const userValueName = mode ? name.split(mode)[1] : name;

      if (chartPrices[userValueName] === valueNumber) {
        return;
      }

      TrackingHandler.fireEvent(userValueName === 'userMsrp' ? 'pc_msrp_input' : 'pc_dealer_price_input', {
        event_data: {
          value: valueNumber,
          action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
          creative_id: PRICE_CHECKER_CREATIVE_ID,
        },
      });
    },
    [chartPrices, mode]
  );

  const onInputKeyPress = useCallback(
    e => {
      if (e.key === ENTER_KEY_VALUE) {
        onInputBlur(e);
      }
    },
    [onInputBlur]
  );

  const onInputChange = useCallback(
    ({ target }) => {
      const userValueName = mode ? target.name.split(mode)[1] : target.name;
      const value = Number(get(target, 'value', 0));

      setInputValues({
        ...inputValues,
        [userValueName]: value,
      });
      setInputErrors({
        ...inputErrors,
        [userValueName]: value > PRICE_THRESHOLD ? PRICE_ERRORS.EXCEED_MAXIMUM[userValueName] : null,
      });
      setSubmitErrors({
        userMsrp: null,
        userDealerPrice: null,
      });
    },
    [inputErrors, inputValues, mode]
  );

  const focusErrorField = useMemo(
    () => debounce(formElement => formElement?.querySelector('.has-danger')?.focus(), 50),
    []
  );

  const getSubmitErrors = useCallback(
    ({ userMsrp: submittedUserMsrp, userDealerPrice: submittedUserDealerPrice, selectedDealer }) => {
      const handlePriceErrors = get(sharedWithCommunityProps, 'handlePriceErrors');

      let msrpError;
      let dealerPriceError;

      if (
        submittedUserMsrp <= 0 ||
        (defaultMsrp && (submittedUserMsrp < defaultMsrp * 0.75 || submittedUserMsrp > defaultMsrp * 1.35))
      ) {
        msrpError = PRICE_ERRORS.OUT_OF_RANGE.userMsrp;
      }

      if (
        submittedUserDealerPrice <= 0 ||
        (defaultMsrp &&
          (submittedUserDealerPrice < defaultMsrp * 0.65 || submittedUserDealerPrice > defaultMsrp * 1.45))
      ) {
        dealerPriceError = PRICE_ERRORS.OUT_OF_RANGE.userDealerPrice;
      }

      if (msrpError) {
        TrackingHandler.fireEvent('pc_msrp_input_error', {
          event_data: {
            value: submittedUserMsrp,
            action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
            creative_id: PRICE_CHECKER_CREATIVE_ID,
          },
        });
      }

      if (dealerPriceError) {
        TrackingHandler.fireEvent('pc_dealer_price_error', {
          event_data: {
            value: submittedUserDealerPrice,
            action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
            creative_id: PRICE_CHECKER_CREATIVE_ID,
          },
        });
      }

      const hasError = !!(msrpError || dealerPriceError);
      if (handlePriceErrors) {
        handlePriceErrors(hasError);
      }

      setSubmitErrors({
        userMsrp: msrpError,
        userDealerPrice: dealerPriceError,
        dealerSelectError: selectedDealer ? null : true,
      });
      focusErrorField(innerRef?.current);
    },
    [defaultMsrp, focusErrorField, sharedWithCommunityProps]
  );

  const onSubmit = useCallback(
    e => {
      e.preventDefault();

      setTimeout(() => {
        if (get(innerRef, 'current[2]')) {
          innerRef.current[2].blur();
        }

        const newUserMsrp = Number(get(e, `target.${mode}userMsrp.value`, 0));
        const newUserDealerPrice = Number(get(e, `target.${mode}userDealerPrice.value`, 0));
        const selectedDealer = get(sharedWithCommunityProps, 'selectedDealer');
        const isDealSubmitted = get(sharedWithCommunityProps, 'isDealSubmitted');

        if (newUserMsrp === userMsrp && newUserDealerPrice === userDealerPrice && isDealSubmitted) {
          return;
        }
        const newSubmitErrors = { userMsrp: newUserMsrp, userDealerPrice: newUserDealerPrice, selectedDealer };

        if (selectedDealer || isCore6830International) {
          setSubmitErrors({ ...submitErrors, dealerSelectError: null });
        } else {
          getSubmitErrors(newSubmitErrors);
          return;
        }

        if (!newUserMsrp || !newUserDealerPrice) {
          getSubmitErrors(newSubmitErrors);
          return;
        }

        setChartPrices({ userMsrp: newUserMsrp, userDealerPrice: newUserDealerPrice });

        getSubmitErrors(newSubmitErrors);
        resetSharedDealParams();
      }, 0);
    },
    [
      innerRef,
      mode,
      sharedWithCommunityProps,
      userMsrp,
      userDealerPrice,
      isCore6830International,
      setChartPrices,
      getSubmitErrors,
      resetSharedDealParams,
      submitErrors,
    ]
  );

  const onReset = useCallback(() => {
    setSubmitErrors({
      userMsrp: null,
      userDealerPrice: null,
    });
    setInputErrors({
      userMsrp: null,
      userDealerPrice: null,
    });
  }, []);

  const paymentInputProps = {
    leftColSizes: { xs: 12 },
    rightColSizes: { xs: 12 },
    leftColClasses: 'text-nowrap',
    rightColClasses: 'mt-0_25',
    labelClassName: 'small',
    tooltipInnerClassName: 'p-1',
    isCurrency: true,
    onBlur: onInputBlur,
    onKeyPress: onInputKeyPress,
    onChange: onInputChange,
    disabled,
  };
  const msrpError = get(errors, 'userMsrp');
  const dealerPriceError = get(errors, 'userDealerPrice');

  return (
    <Form
      className={classnames('prices-form', className)}
      onSubmit={onSubmit}
      onReset={onReset}
      innerRef={innerRef}
      noValidate
    >
      {!isCore6830International && (
        <DealerSelection
          {...communityProps}
          dealerSelectError={errors.dealerSelectError}
          disabled={disabled}
          mode={mode}
        />
      )}
      <Row className="text-gray-darker mb-0_25 mb-md-1 mb-lg-0_25">
        <Col xs={6}>
          <PaymentInput
            label="Dealer price quote"
            name={`${mode}userDealerPrice`}
            forceValue={inputValues.userDealerPrice}
            tooltip={{
              placement: 'bottom',
              text: 'Enter the dealer price quote for the vehicle you are buying including any installed optional equipment, but excluding taxes and fees.',
            }}
            inputClassName={classnames({ 'has-danger': dealerPriceError })}
            {...paymentInputProps}
          />
          <div
            className={classnames('error-msg text-danger label mt-0_25', { invisible: !dealerPriceError })}
            aria-hidden={!dealerPriceError}
          >
            {dealerPriceError}
          </div>
        </Col>
        <Col xs={6}>
          <PaymentInput
            label="MSRP from quote"
            name={`${mode}userMsrp`}
            forceValue={inputValues.userMsrp}
            tooltip={{
              placement: 'bottom',
              text: 'Update this price to the Manufacturer’s Suggested Retail Price for the vehicle you are buying including any installed optional equipment, but excluding taxes and fees. We’ll determine the suggested price to pay based on the Style you selected.',
            }}
            inputClassName={classnames({ 'has-danger': msrpError })}
            readOnly={readOnlyMsrp}
            {...paymentInputProps}
          />
          <div
            className={classnames('error-msg text-danger label mt-0_25', { invisible: !msrpError })}
            aria-hidden={!msrpError}
          >
            {msrpError}
          </div>
        </Col>
      </Row>

      <div className="text-center">
        <Button
          color={isDealRated ? 'outline-primary-b' : 'primary-b'}
          type="submit"
          disabled={isSubmitDisabled}
          size="lg"
          className={classnames('rate-offer-btn text-transform-none px-3 py-0_5', {
            'btn-bg-white': isDealRated,
            'w-100': !isMobile,
          })}
          data-tracking-id="pc_submit_prices"
        >
          {isDealRated ? 'Rate another offer' : 'Rate this dealer offer'}
        </Button>
      </div>
    </Form>
  );
}

PricesFormUI.propTypes = {
  setChartPrices: PropTypes.func.isRequired,
  chartPrices: PropTypes.shape({
    userDealerPrice: PropTypes.number,
    userMsrp: PropTypes.number,
  }).isRequired,
  defaultMsrp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isDealRated: PropTypes.bool,
  className: PropTypes.string,
  innerRef: PropTypes.shape(),
  readOnlyMsrp: PropTypes.bool,
  disabled: PropTypes.bool,
  isMobile: PropTypes.bool,
  resetSharedDealParams: PropTypes.func,
  onChangeDealer: PropTypes.func,
  mode: PropTypes.string,
  sharedWithCommunityProps: PriceCheckerEntities.CommunityDealEntity,
  isCore6830International: PropTypes.bool,
};

export const PricesForm = React.memo(PricesFormUI, isEqual);
